import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { supabase } from '../../lib/supabase';

interface FollowUpStats {
  submission_type: string;
  total_contacts: number;
  follow_up_enabled_count: number;
  responded_count: number;
  response_rate_percentage: number;
  avg_follow_ups_sent: number;
  total_follow_ups_sent: number;
  pending_follow_ups: number;
}

interface ContactWithFollowUps {
  id: string;
  full_name: string;
  email: string;
  message: string;
  submission_type: string;
  created_at: string;
  follow_up_enabled: boolean;
  follow_up_count: number;
  last_follow_up_sent_at: string | null;
  user_responded: boolean;
  user_responded_at: string | null;
  follow_up_sequences: Array<{
    id: string;
    sequence_number: number;
    status: string;
    scheduled_for: string;
    sent_at: string | null;
  }>;
}

interface EmailResponse {
  id: string;
  from_email: string;
  subject: string;
  body: string;
  received_at: string;
}

export default function FollowUpManager() {
  const [stats, setStats] = useState<FollowUpStats[]>([]);
  const [contacts, setContacts] = useState<ContactWithFollowUps[]>([]);
  const [selectedContact, setSelectedContact] = useState<ContactWithFollowUps | null>(null);
  const [responses, setResponses] = useState<EmailResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch statistics
      const { data: statsData, error: statsError } = await supabase
        .from('follow_up_statistics')
        .select('*');

      if (statsError) throw statsError;
      setStats(statsData || []);

      // Fetch contacts with follow-ups
      const { data: contactsData, error: contactsError } = await supabase
        .from('contact_submissions')
        .select(`
          *,
          follow_up_sequences (
            id,
            sequence_number,
            status,
            scheduled_for,
            sent_at
          )
        `)
        .eq('submission_type', 'mentorship')
        .order('created_at', { ascending: false })
        .limit(50);

      if (contactsError) throw contactsError;
      setContacts(contactsData || []);
    } catch (err: any) {
      console.error('Error fetching follow-up data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (contact: ContactWithFollowUps) => {
    setSelectedContact(contact);
    
    // Fetch email responses for this contact
    const { data: responsesData } = await supabase
      .from('email_responses')
      .select('*')
      .eq('contact_submission_id', contact.id)
      .order('received_at', { ascending: false });

    setResponses(responsesData || []);
    setDialogOpen(true);
  };

  const handleToggleFollowUp = async (contactId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('contact_submissions')
        // @ts-ignore - Supabase types are correct at runtime
        .update({ follow_up_enabled: !currentStatus })
        .eq('id', contactId);

      if (error) throw error;

      // If disabling, cancel pending follow-ups
      if (currentStatus) {
        await supabase
          .from('follow_up_sequences')
          // @ts-ignore - Supabase types are correct at runtime
          .update({ status: 'cancelled' })
          .eq('contact_submission_id', contactId)
          .eq('status', 'pending');
      }

      fetchData();
    } catch (err: any) {
      console.error('Error toggling follow-up:', err);
      setError(err.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'success';
      case 'pending': return 'warning';
      case 'responded': return 'info';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  const mentorshipStats = stats.find(s => s.submission_type === 'mentorship');

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 900, color: '#dc143c' }}>
        📧 FOLLOW-UP SYSTEM
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{ bgcolor: '#1a1a1a', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ color: '#dc143c', mr: 1 }} />
                <Typography variant="h6">Total Contacts</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900 }}>
                {mentorshipStats?.total_contacts || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                Mentorship requests
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{ bgcolor: '#dc143c', color: 'white', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Response Rate</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900 }}>
                {mentorshipStats?.response_rate_percentage || 0}%
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {mentorshipStats?.responded_count || 0} responded
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{ bgcolor: '#f5f5f5', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#dc143c', mr: 1 }} />
                <Typography variant="h6">Avg Follow-ups</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900, color: '#1a1a1a' }}>
                {mentorshipStats?.avg_follow_ups_sent?.toFixed(1) || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Per contact
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{ bgcolor: '#f5f5f5', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScheduleIcon sx={{ color: '#dc143c', mr: 1 }} />
                <Typography variant="h6">Pending</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 900, color: '#1a1a1a' }}>
                {mentorshipStats?.pending_follow_ups || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Scheduled follow-ups
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Contacts Table */}
      <Paper sx={{ p: 3, bgcolor: 'white' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#1a1a1a', mb: 3 }}>
          Mentorship Contacts
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Follow-ups Sent</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Last Follow-up</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  sx={{
                    '&:hover': { bgcolor: '#f9f9f9' },
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    {contact.full_name}
                  </TableCell>
                  <TableCell sx={{ color: '#666' }}>
                    {contact.email}
                  </TableCell>
                  <TableCell>
                    {contact.user_responded ? (
                      <Chip
                        label="Responded"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                    ) : contact.follow_up_enabled ? (
                      <Chip
                        label="Active"
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Inactive"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${contact.follow_up_count || 0} / 8`}
                      size="small"
                      sx={{ bgcolor: '#dc143c', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#666' }}>
                    {contact.last_follow_up_sent_at
                      ? new Date(contact.last_follow_up_sent_at).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleViewDetails(contact)}
                      sx={{ mr: 1 }}
                    >
                      View Details
                    </Button>
                    <Button
                      size="small"
                      color={contact.follow_up_enabled ? 'error' : 'success'}
                      onClick={() => handleToggleFollowUp(contact.id, contact.follow_up_enabled)}
                      disabled={contact.user_responded}
                    >
                      {contact.follow_up_enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1a1a1a', color: 'white' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Follow-up Details: {selectedContact?.full_name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedContact && (
            <Box>
              {/* Original Message */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Original Message:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedContact.message}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                  Submitted: {new Date(selectedContact.created_at).toLocaleString()}
                </Typography>
              </Paper>

              {/* Follow-up Sequence */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Follow-up Sequence:
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Scheduled</strong></TableCell>
                      <TableCell><strong>Sent</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedContact.follow_up_sequences
                      .sort((a, b) => a.sequence_number - b.sequence_number)
                      .map((seq) => (
                        <TableRow key={seq.id}>
                          <TableCell>{seq.sequence_number}</TableCell>
                          <TableCell>
                            <Chip
                              label={seq.status}
                              color={getStatusColor(seq.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(seq.scheduled_for).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {seq.sent_at
                              ? new Date(seq.sent_at).toLocaleDateString()
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Email Responses */}
              {responses.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Email Responses:
                  </Typography>
                  {responses.map((response) => (
                    <Paper key={response.id} sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {response.subject}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {response.body}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                        Received: {new Date(response.received_at).toLocaleString()}
                      </Typography>
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}